import React, { SetStateAction, useCallback, useState } from "react";
import { GridRowClassNameParams, GridRowId, GridRowParams } from "@mui/x-data-grid-pro";
import { AnyError } from "src/errorHandling/anyError";
import { ErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialog.tsx";
import { openFormForDataGridRow } from "src/components/common/dataGrid/dataGridFormUtils";
import {
	DataGridValidationContextProvider,
	DataGridValidationErrors,
} from "src/components/common/dataGrid/crud/validation/DataGridValidationContext.tsx";
import { AavoDataGrid, AavoDataGridProps } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { dataGridRowClassNames } from "src/components/common/dataGrid/styles/dataGridClassNames.ts";
import { useContextOrThrow } from "src/utils/useContextOrThrow.tsx";
import { GenericDialogContext, OpenGenericDialogFunc } from "../../dialogs/GenericDialogProvider";
import { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import { v4 as uuidv4 } from "uuid";
import { CrudActionButtonDefinition } from "src/components/common/crud/crudActionButtonDefinition.ts";
import i18n from "i18next";
import { CrudActionButtons } from "../../crud/CrudActionButtons";
import { useForwardedRef } from "src/utils/useForwardedRef.ts";
import { DataGridFormDefinition } from "src/components/common/dataGrid/types.tsx";
import { GridRowIdGetter } from "@mui/x-data-grid/models/gridRows";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";
import { isNotNullOrEmptyObject } from "src/utils/objectUtils.ts";

export interface CrudDataGridProps<RowData extends object>
	extends Omit<AavoDataGridProps<RowData>, "getRowId"> {
	refreshData: () => Promise<unknown>;
	add?: CrudActionButtonDefinition<void, Partial<RowData>>;
	save?: CrudActionButtonDefinition<
		{
			items: RowData[];
		},
		RowData[]
	>;
	remove?: CrudActionButtonDefinition<{
		items: RowData[];
	}>;
	actionBarComponents?: React.ReactElement;
	form?: DataGridFormDefinition<RowData>;
	getRowId: GridRowIdGetter<RowData>;
}

interface CrudDataGridModel<RowData extends object>
	extends Pick<CrudDataGridProps<RowData>, "form" | "editMode" | "add" | "save" | "remove"> {
	getGridApi: () => GridApiPro;
	getRowId: (row: RowData | NewRowData<RowData>) => GridRowId;
	selectedRows: RowData[];
	setSelectedRows: (rows: RowData[]) => void;
	rowUnderEdit: GridRowId | undefined;
	setRowUnderEdit: React.Dispatch<SetStateAction<GridRowId | undefined>>;
	editedRows: GridRowId[];
	setEditedRows: React.Dispatch<SetStateAction<GridRowId[]>>;
	newRows: GridRowId[];
	setNewRows: React.Dispatch<SetStateAction<GridRowId[]>>;
	error: AnyError | undefined;
	setError: React.Dispatch<SetStateAction<AnyError | undefined>>;
	refreshData: () => Promise<unknown>;
	hasAnyValidationErrors: () => boolean;
	openDialog: OpenGenericDialogFunc;
}

type NewRowData<RowData extends object> = {
	__generated_id: string;
	isNewRow: true;
} & Partial<RowData>;

export const CrudDataGrid = <RowData extends object>({
	refreshData: refreshDataProp,
	getRowId: getRowIdProp,
	add,
	save,
	remove,
	form,
	actionBarComponents,
	apiRef: forwardedApiRef,
	selectedRows: selectedRowsProp,
	onRowSelectionChanged: onRowSelectionChangedProp,
	rowCount,
	editMode = "row",
	onCellEditStart,
	onCellEditStop,
	onRowEditStart,
	onRowEditStop,
	getRowClassNames: getRowClassNamesProp,
	...other
}: CrudDataGridProps<RowData>) => {
	validateActionParameters(add, save, form);

	const { openDialog } = useContextOrThrow(GenericDialogContext);
	const gridApiRef = useForwardedRef(forwardedApiRef);
	const [error, setError] = useState<AnyError | undefined>(undefined);

	const [rowUnderEdit, setRowUnderEdit] = useState<GridRowId | undefined>(undefined);
	const [editedRows, setEditedRows] = useState<GridRowId[]>([]);
	const [unsavedNewRows, setUnsavedNewRows] = useState<GridRowId[]>([]);
	const [selectedRows, setSelectedRows] = useMaybeControlledState<RowData[]>({
		controlledValue: selectedRowsProp,
		onChange: onRowSelectionChangedProp,
		defaultValue: [],
	});

	const [validationErrors, setValidationErrors] = React.useState<DataGridValidationErrors>({});

	const getGridApi = (): GridApiPro => {
		const current = gridApiRef.current;
		if (isNotNullOrEmptyObject(current)) {
			return current;
		} else {
			const error = "GridApiRef has no value";
			console.error(error);
			throw new Error(error);
		}
	};

	const getRowId = wrapGetRowId(getRowIdProp);

	const refreshData = async () => {
		await refreshDataProp();
		setUnsavedNewRows([]);
		setEditedRows([]);
	};

	const hasAnyValidationErrors = () =>
		Object.values(validationErrors).some((rowErrors) =>
			Object.values(rowErrors).some((fieldError) => fieldError !== undefined),
		);

	const model: CrudDataGridModel<RowData> = {
		getGridApi,
		getRowId,
		form,
		editMode,
		add,
		save,
		remove,
		selectedRows,
		setSelectedRows,
		rowUnderEdit,
		setRowUnderEdit,
		editedRows,
		setEditedRows,
		newRows: unsavedNewRows,
		setNewRows: setUnsavedNewRows,
		error,
		setError,
		refreshData,
		hasAnyValidationErrors,
		openDialog,
	};

	const handleCloseErrorDialog = useCallback(() => {
		setError(undefined);
	}, [setError]);

	const rowCountWithUnsavedRows = (rowCount ?? 0) + unsavedNewRows.length;

	return (
		<>
			<ErrorDialog error={error} onClose={handleCloseErrorDialog} />
			<DataGridValidationContextProvider setErrors={setValidationErrors}>
				<AavoDataGrid
					rowCount={rowCountWithUnsavedRows}
					apiRef={gridApiRef}
					getRowId={getRowId}
					editMode={editMode}
					onRefresh={async () => {
						await onRefresh(model);
					}}
					actionBarComponents={
						<>
							<CrudActionButtons
								add={getAddAction(model)}
								save={getSaveAction(model)}
								remove={getRemoveAction(model)}
								edit={getEditAction(model)}
							/>
							{actionBarComponents}
						</>
					}
					onCellEditStart={(params, event, details) => {
						onCellEditStart?.(params, event, details);
						handleRowEditStarted(getRowId(params.row), isNewRow(params.row), model);
					}}
					onCellEditStop={(params, event, details) => {
						onCellEditStop?.(params, event, details);
						setRowUnderEdit(undefined);
					}}
					onRowEditStart={(params, event, details) => {
						onRowEditStart?.(params, event, details);
						handleRowEditStarted(getRowId(params.row), isNewRow(params.row), model);
					}}
					onRowEditStop={(params, event, details) => {
						onRowEditStop?.(params, event, details);
						setRowUnderEdit(undefined);
					}}
					getRowClassNames={(params) =>
						getRowClassNames(getRowClassNamesProp, params, model)
					}
					selectedRows={selectedRows}
					onRowSelectionChanged={setSelectedRows}
					onRowDoubleClick={async (params: GridRowParams<RowData>) => {
						await openFormForRow(params.row, model);
					}}
					{...other}
				/>
			</DataGridValidationContextProvider>
		</>
	);
};

const validateActionParameters = (
	add?: CrudActionButtonDefinition<any, any>,
	save?: CrudActionButtonDefinition<any, any>,
	form?: DataGridFormDefinition<any>,
) => {
	const hasAddAction = add !== undefined && add.type !== "hidden";
	const hasSaveAction = save !== undefined && save.type !== "hidden";
	const hasSaveOrAddAction = hasAddAction || hasSaveAction;
	if (form !== undefined && hasSaveOrAddAction) {
		console.warn(
			"Form was defined to table, together with add or save action. " +
				"Add and save action definition does not make sense if the form is defined and thus can cause errors.",
		);
	}
};

const wrapGetRowId = <RowData extends object>(getRowId: GridRowIdGetter<RowData>) => {
	return (row: RowData | NewRowData<RowData>) => {
		if (isNewRow(row)) {
			return row.__generated_id;
		} else {
			return getRowId(row);
		}
	};
};

const getAddAction = <RowData extends object>(
	model: CrudDataGridModel<RowData>,
): CrudActionButtonDefinition => {
	const { form, add, error, setError } = model;

	if (form !== undefined) {
		if (!form.addRowEnabled) return { type: "hidden" };
		if (error !== undefined)
			return {
				type: "disabled",
			};

		return {
			type: "enabled",
			action: async () => {
				await openFormForRow(undefined, model);
			},
		};
	}

	if (add?.type === "enabled") {
		return {
			type: add.type,
			action: async () => {
				const newRow = await add.action();
				if (newRow !== "interrupt") {
					handleAddNewLocalUnsavedRow(newRow, model);
				} else {
					setError(i18n.t("action_interrupted"));
				}
			},
		};
	}

	return {
		type: "hidden",
	};
};

const getSaveAction = <RowData extends object>(
	model: CrudDataGridModel<RowData>,
): CrudActionButtonDefinition => {
	const {
		form,
		save,
		newRows,
		editedRows,
		rowUnderEdit,
		refreshData,
		error,
		hasAnyValidationErrors,
	} = model;

	if (form !== undefined) return { type: "hidden" };

	if (save?.type !== "enabled") return save ?? { type: "hidden" };

	if (error !== undefined || hasAnyValidationErrors())
		return {
			type: "disabled",
		};

	if (!newRows.length && !editedRows.length && rowUnderEdit === undefined)
		return {
			type: "disabled",
			tooltip: i18n.t("no_unsaved_or_edited_rows"),
		};

	return {
		type: save.type,
		action: async () => {
			const changedRows = getLocallyChangedRows(model);
			await save.action({ items: changedRows });
			await refreshData();
		},
	};
};

const getRemoveAction = <RowData extends object>(
	model: CrudDataGridModel<RowData>,
): CrudActionButtonDefinition => {
	const {
		getGridApi,
		remove,
		selectedRows,
		error,
		setError,
		refreshData,
		getRowId,
		setEditedRows,
		setNewRows,
	} = model;

	if (remove?.type !== "enabled") return remove ?? { type: "hidden" };

	if (error !== undefined)
		return {
			type: "disabled",
		};

	if (selectedRows.length === 0)
		return {
			type: "disabled",
			tooltip: i18n.t("select_at_least_one_row"),
		};

	return {
		type: remove.type,
		action: async () => {
			const gridApi = getGridApi();
			const unsavedSelectedRows = selectedRows.filter((r) => isNewRow(r));
			const savedSelectedRows = selectedRows.filter((r) => !isNewRow(r));

			// Execute delete on remove server.
			if (savedSelectedRows.length > 0) {
				const result = await remove.action({
					items: savedSelectedRows,
				});
				if (result === "interrupt") {
					setError(i18n.t("action_interrupted"));
					return;
				} else {
					await refreshData();
				}
			}

			// Delete rows locally from grid.
			const localRowsToRemove = [...unsavedSelectedRows, ...savedSelectedRows];
			gridApi.updateRows(
				localRowsToRemove.map((r) => {
					return {
						_action: "delete",
						...r,
					};
				}),
			);

			// Remove deleted rows form dirty row states.
			const deletedRowIds = selectedRows.map((r) => getRowId(r));
			setEditedRows((prev) => prev.filter((r) => !deletedRowIds.includes(r)));
			setNewRows((prev) => prev.filter((r) => !deletedRowIds.includes(r)));
		},
	};
};

const getEditAction = <RowData extends object>(
	model: CrudDataGridModel<RowData>,
): CrudActionButtonDefinition => {
	const { form, selectedRows, error } = model;

	if (form === undefined || !form.editEnabled) return { type: "hidden" };

	if (selectedRows.length !== 1)
		return {
			type: "disabled",
			tooltip: i18n.t("select_a_single_row"),
		};

	if (error !== undefined)
		return {
			type: "disabled",
		};

	return {
		type: "enabled",
		action: async () => {
			const firstRow = selectedRows[0];
			if (firstRow !== null) {
				await openFormForRow(firstRow, model);
			}
		},
	};
};

const onRefresh = async <RowData extends object>({
	getGridApi,
	newRows,
	refreshData,
}: CrudDataGridModel<RowData>) => {
	const gridApi = getGridApi();
	// Bug in MUIGridPro requires that editing of unsaved rows is stopped manually before refresh.
	newRows
		.filter((rowId) => gridApi.getRowMode(rowId) === "edit")
		.forEach((rowId) => {
			gridApi.stopRowEditMode({
				id: rowId,
				ignoreModifications: true,
			});
		});
	await refreshData();
};

const openFormForRow = async <RowData extends object>(
	row: RowData | undefined,
	model: CrudDataGridModel<RowData>,
) => {
	if (model.form === undefined) return;

	await openFormForDataGridRow({
		row: row,
		form: model.form,
		gridApi: model.getGridApi(),
		openDialog: model.openDialog,
		refreshData: model.refreshData,
	});
};

const handleAddNewLocalUnsavedRow = <RowData extends object>(
	newRow: Partial<RowData>,
	model: CrudDataGridModel<RowData>,
) => {
	const { getGridApi, setNewRows } = model;
	const gridApi = getGridApi();

	const newRowGeneratedId = uuidv4();
	gridApi.updateRows([
		{
			...newRow,
			isNewRow: true,
			__generated_id: newRowGeneratedId,
		},
	]);
	setNewRows((prev) => {
		return [...prev, newRowGeneratedId];
	});
	startToEditNewRow<RowData>(newRowGeneratedId, model);
};

const startToEditNewRow = <RowData extends object>(
	rowId: GridRowId,
	model: CrudDataGridModel<RowData>,
) => {
	const { getGridApi, editMode } = model;
	const gridApi = getGridApi();

	const firstEditableField = gridApi.getAllColumns().find((c) => c.editable)?.field;
	if (!firstEditableField) return;

	if (editMode === "row")
		gridApi.startRowEditMode({
			id: rowId,
			fieldToFocus: firstEditableField,
		});
	else if (editMode === "cell")
		gridApi.startCellEditMode({
			id: rowId,
			field: firstEditableField,
		});

	handleRowEditStarted(rowId, true, model);
};

const handleRowEditStarted = <RowData extends object>(
	rowId: GridRowId,
	isNewRow: boolean,
	{ setRowUnderEdit, setEditedRows }: CrudDataGridModel<RowData>,
) => {
	setRowUnderEdit(rowId);
	if (!isNewRow) {
		setEditedRows((prev) => {
			if (prev.includes(rowId)) {
				return prev;
			} else {
				return [...prev, rowId];
			}
		});
	}
};

const getLocallyChangedRows = <RowData extends object>({
	getGridApi,
	rowUnderEdit,
	editedRows,
	newRows,
}: CrudDataGridModel<RowData>): RowData[] => {
	const gridApi = getGridApi();
	const currentlyEditedRow =
		rowUnderEdit !== undefined ? gridApi.getRowWithUpdatedValues(rowUnderEdit, "") : undefined;

	const uniqueRows = [...editedRows, ...newRows.filter((id) => !editedRows.includes(id))]
		.filter((id) => id !== rowUnderEdit)
		.map((id) => {
			return gridApi.getRow(id);
		})
		.filter((r) => r !== null && r !== undefined);

	return currentlyEditedRow !== undefined ? [currentlyEditedRow, ...uniqueRows] : uniqueRows;
};

const getRowClassNames = <RowData extends object>(
	getRowClassNamesProp: CrudDataGridProps<RowData>["getRowClassNames"],
	params: GridRowClassNameParams,
	{ editedRows }: CrudDataGridModel<RowData>,
): string[] => {
	const ret = [];

	const fromProp = getRowClassNamesProp?.(params) ?? [];
	if (typeof fromProp === "string") ret.push(fromProp);
	else ret.push(...fromProp);

	if (params.row.isNewRow) {
		ret.push(dataGridRowClassNames.unsavedNew);
	} else if (editedRows.includes(params.id)) {
		ret.push(dataGridRowClassNames.edited);
	}
	return ret;
};

const isNewRow = <T extends object>(value: T | NewRowData<T>): value is NewRowData<T> => {
	return (value as NewRowData<T>).isNewRow;
};
